import styles from './ordersMetrics.module.scss';

/**
 * Money from `getOrdersMetrics` is always a LIST, one entry per currency:
 *
 *   [{currency: 'XOF', amount: 27440}, {currency: 'NGN', amount: 5000}]
 *
 * ⚠️ The entries are never added together. Orders carry their own currency and
 * one scope can span several, so a cross-currency total is not a number that
 * means anything. Every money figure on this screen goes through here so that
 * rule cannot be broken by accident in one card.
 *
 * An empty list is zero, not loading.
 */

const format = amount =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount ?? 0);

/** One `{currency, amount}` row. */
const Row = ({entry, signed}) => {
  const amount = entry?.amount ?? 0;
  const sign = signed && amount > 0 ? '+' : '';
  return (
    <div className={styles.moneyRow}>
      <span className={styles.moneyCurrency}>{entry?.currency}</span>
      <span
        className={`${styles.moneyAmount} ${
          signed ? (amount < 0 ? styles.moneyDown : styles.moneyUp) : ''
        }`}>
        {sign}
        {format(amount)}
      </span>
    </div>
  );
};

export const Money = ({value, signed = false, emptyLabel = '0'}) => {
  const entries = Array.isArray(value) ? value : [];

  if (!entries.length)
    return <span className={styles.moneyEmpty}>{emptyLabel}</span>;

  return (
    <div className={styles.moneyStack}>
      {entries.map(entry => (
        <Row key={entry?.currency} entry={entry} signed={signed} />
      ))}
    </div>
  );
};

/** The currencies present anywhere in a set of money lists. */
export const currenciesIn = (...lists) => {
  const seen = [];
  lists.forEach(list => {
    (Array.isArray(list) ? list : []).forEach(entry => {
      if (entry?.currency && seen.indexOf(entry.currency) === -1)
        seen.push(entry.currency);
    });
  });
  return seen;
};

export default Money;
